<template>
  <Teleport to="body">
    <div
      v-if="isVisible"
      :class="['toast-container', { 'show-toast': showToast, 'hide-toast': !showToast }]"
    >
      <div :class="['toast', variant]">
        <Icon v-if="icon" :name="icon" />
        <span>{{ modelValue }}</span>
      </div>
    </div>
  </Teleport>
</template>

<script lang="ts" setup>
import { IconName } from '../../../types/icon'
import Icon from '../Icon'
import { computed, watch, ref } from 'vue'

export interface ToastProps {
  modelValue: string
  variant?: 'default' | 'info' | 'success' | 'warning' | 'danger'
  duration?: number
}

const emit = defineEmits<{
  'update:modelValue': [value: string]
}>()

const {
  modelValue,
  variant = 'default',
  duration = 3000,
} = defineProps<ToastProps>()

const animationTime = '580ms'
const isVisible = ref(false)
const showToast = ref(true)

const availableIcons = {
  default: 'clock-line-1',
  info: 'info-line',
  success: 'clock-line',
  warning: 'warning-line',
  danger: 'error-line',
}

const icon = computed(() => availableIcons[variant] as IconName)

watch(() => modelValue, (value) => {
  isVisible.value = !!value

  if (value) {
    showToast.value = true
    setTimeout(() => {
      hideToast()
    }, duration)
  }
})

function hideToast() {
  showToast.value = false
  setTimeout(() => {
    emit('update:modelValue', '')
  }, parseFloat(animationTime.replace('ms', '')) * 0.9)
}
</script>

<style scoped lang="scss">
  .toast-container {
    position: fixed;
    top: 0;
    right: 0;
    display: flex;
    width: 100%;
    justify-content: center;
    padding-top: 0.667rem;
    animation-duration: v-bind('animationTime');
    animation-timing-function: cubic-bezier(.09,-0.49,.9,1.5);
    z-index: 99998;

    &.show-toast {
      animation-name: in;
    }

    &.hide-toast {
      animation-name: out;
    }
  }

  .toast {
    display: flex;
    padding: .5rem 1rem .5rem 1rem;
    border-radius: 2rem;
    align-items: center;
    justify-content: center;
    gap: .6rem;
    background-color: var(--neutral-80);
    color: var(--white);
    box-shadow: 0 .15rem .15rem rgb(16 82 82 / 20%);

    &.info {
      background-color: var(--info-light);
      color: var(--info);
    }

    &.success {
      background-color: var(--success-light);
      color: var(--success);
    }

    &.warning {
      background-color: var(--warning-light);
      color: var(--warning);
    }

    &.danger {
      background-color: var(--danger-light);
      color: var(--danger);
    }
  }

  @keyframes in {
    0%   { right: -100vw }
    100% { right: 0 }
  }

  @keyframes out {
    0%   { right: 0 }
    100% { right: 100vw }
  }
</style>
